<template>
  <v-container class="d-flex align-center justify-center" style="min-height: 60vh;">
    <v-card width="520">
      <v-card-title class="d-flex align-center pa-6 pb-4">
        <v-icon class="mr-2">fas fa-sitemap</v-icon>
        Create Your Organization
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <p class="text-body-2 text-medium-emphasis mb-6">
          Setting up an organization lets you invite teammates, assign roles, and manage shared SDK keys under one account.
          Your existing subscription and SDK keys will transfer to the organization automatically.
        </p>

        <v-text-field
          v-model="displayName"
          label="Organization Name"
          variant="outlined"
          density="compact"
          placeholder="Acme Corp"
          class="mb-4"
          :error-messages="displayNameError"
          @input="syncSlug"
        ></v-text-field>

        <v-text-field
          v-model="slug"
          label="Organization ID"
          variant="outlined"
          density="compact"
          placeholder="acme-corp"
          hint="Lowercase letters, numbers, and hyphens only. Used internally by Auth0."
          persistent-hint
          class="mb-2"
          :error-messages="slugError"
          @input="slugTouched = true"
        ></v-text-field>

        <v-alert v-if="apiError" type="error" density="compact" class="mt-4">
          {{ apiError }}
        </v-alert>
      </v-card-text>

      <v-card-actions class="pa-6 pt-0">
        <v-btn variant="text" @click="cancel">Cancel</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="elevated"
          :loading="submitting"
          :disabled="!isValid"
          @click="submit"
        >
          Create Organization
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Success dialog -->
    <v-dialog v-model="showSuccess" max-width="460" persistent>
      <v-card>
        <v-card-title class="d-flex align-center pt-6 px-6">
          <v-icon color="success" class="mr-2">fas fa-check-circle</v-icon>
          Organization Created!
        </v-card-title>
        <v-card-text class="px-6">
          <p><strong>{{ createdDisplayName }}</strong> has been created and your subscription has been transferred.</p>
          <p class="mt-3 text-body-2 text-medium-emphasis">
            You'll be redirected to sign in again so your account reflects the new organization.
          </p>
        </v-card-text>
        <v-card-actions class="pa-6 pt-2">
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="elevated" @click="refreshSession">Continue</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import axios from 'axios';

const BASE = `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/organizations/`;
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;

function toSlug(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 63)
    .replace(/^-+|-+$/g, '');
}

export default {
  name: 'OrgCreationView',
  setup() {
    const router = useRouter();
    const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

    const displayName = ref('');
    const slug = ref('');
    const slugTouched = ref(false);
    const submitting = ref(false);
    const apiError = ref(null);
    const showSuccess = ref(false);
    const createdDisplayName = ref('');

    const displayNameError = computed(() =>
      displayName.value.length > 0 && displayName.value.trim().length < 2
        ? 'Name must be at least 2 characters.'
        : ''
    );

    const slugError = computed(() => {
      if (!slug.value) {
        return slugTouched.value ? 'Organization ID is required.' : '';
      }
      return SLUG_RE.test(slug.value)
        ? ''
        : 'Must be 3–63 characters: lowercase letters, numbers, hyphens (no leading/trailing hyphen).';
    });

    const isValid = computed(() =>
      displayName.value.trim().length >= 2 &&
      SLUG_RE.test(slug.value) &&
      !slugError.value
    );

    function syncSlug() {
      if (!slugTouched.value) {
        slug.value = toSlug(displayName.value);
      }
    }

    function cancel() {
      router.back();
    }

    async function submit() {
      apiError.value = null;
      submitting.value = true;
      try {
        const token = await getAccessTokenSilently();
        await axios.post(
          BASE,
          { name: slug.value, display_name: displayName.value.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        createdDisplayName.value = displayName.value.trim();
        showSuccess.value = true;
      } catch (err) {
        const detail = err.response?.data?.detail;
        apiError.value =
          err.response?.status === 409
            ? 'That organization ID is already taken. Try a different one.'
            : err.response?.status === 403
            ? 'Your subscription plan does not include organization features. Please upgrade.'
            : detail || 'Failed to create organization. Please try again.';
      } finally {
        submitting.value = false;
      }
    }

    function refreshSession() {
      loginWithRedirect({
        appState: { target: '/console/organization' },
      });
    }

    return {
      displayName, slug, slugTouched,
      displayNameError, slugError, isValid,
      submitting, apiError,
      showSuccess, createdDisplayName,
      syncSlug, cancel, submit, refreshSession,
    };
  },
};
</script>
